import { HttpErrorResponse } from '@angular/common/http';
import { isAttachmentScanPendingError } from '@interacta-shared/data-access-error';
import { escapeHtml } from '@interacta-shared/util-common';
import {
    AISharedGenericReference,
    AISharedMessageType,
    AISharedModelMessage,
    AISharedReferenceAvailability,
    AISharedReferenceType,
    aiSharedErrorTypes,
} from '../models/ai-shared.model';
import { AIReferenceTranslate } from './ai-shared-delta-reference.util';

const HEADING_CLASSES: Record<number, string> = {
    1: 'typo-t5',
    2: 'typo-t6',
    3: 'typo-medium-bold',
    4: 'typo-sm-bold',
    5: 'typo-sm-bold',
    6: 'typo-sm-bold',
};

export function formatMessage(message: string): string {
    //! Do not change the order
    // Escaping must run first: the passes below emit the only markup allowed.
    message = escapeHtml(message)
        // Headings must run before bold: strip ** markers inside heading lines
        // instead of styling them, since the heading class already sets the weight.
        // List items must run before newlines: the \n terminator in "* item\n"
        // would be consumed by the \n→<br> pass otherwise.
        .replace(
            /^(#{1,6}) (.*?)$/gm,
            (_: string, hashes: string, content: string) => {
                const cls = HEADING_CLASSES[hashes.length];
                return `<span class="${cls}">${content.replace(/\*\*(.*?)\*\*/gs, '$1')}</span>`;
            },
        )
        .replace(/\*\*(.*?)\*\*/gs, '<strong class="typo-sm-bold">$1</strong>')
        .replace(/((?:\* .+\n)+)/g, (block) => {
            const items = block.replace(/\* (.*?)\n/g, '<li>$1</li>');
            return `<ul class="list-disc pl-20">${items}</ul>`;
        })
        .replace(/^---$/gm, '<hr class="border-t border-border-300">')
        .replace(/\n/g, '<br>');

    return message;
}

// The reference preview line is a single ellipsized row: newlines from the post
// description / comment text would otherwise be rendered as lost whitespace.
function toSingleLinePreview(text: string | null | undefined): string {
    return text?.replace(/\s*\n+\s*/g, ' ').trim() ?? '';
}

export function getReferenceTitle(
    reference: AISharedGenericReference,
    translate: AIReferenceTranslate,
): string {
    switch (reference.type) {
        case AISharedReferenceType.POST:
            return reference.post.title || translate('POST.NO_TITLE');
        case AISharedReferenceType.COMMENT:
            return translate('AI.REFERENCE_COMMENT_TITLE', {
                author: reference.comment.creatorUser?.fullName,
            });
        default:
            return reference.attachment.attachmentData.name;
    }
}

export function getReferencePreviewText(
    reference: AISharedGenericReference,
    translate: AIReferenceTranslate,
): string {
    switch (reference.type) {
        case AISharedReferenceType.POST:
            return toSingleLinePreview(reference.post.description);
        case AISharedReferenceType.COMMENT:
            return toSingleLinePreview(reference.comment.commentPlainText);
        default:
            return translate('AI.REFERENCE_ATTACHMENT_IN_POST', {
                title:
                    reference.attachment.post.title ||
                    translate('POST.NO_TITLE'),
            });
    }
}

type AISharedReferenceUnavailableReason =
    'DELETED' | 'ARCHIVED' | 'NO_PERMISSION';

function getReferenceUnavailableReason(
    reference: AISharedReferenceAvailability,
): AISharedReferenceUnavailableReason | null {
    if (reference.canView) return null;
    if (reference.deleted) return 'DELETED';
    if (reference.archived) return 'ARCHIVED';
    return 'NO_PERMISSION';
}

/** i18n key of the badge shown instead of the preview line, `null` when the source is viewable. */
export function getReferenceUnavailableLabel(
    reference: AISharedReferenceAvailability,
): string | null {
    const reason = getReferenceUnavailableReason(reference);
    return reason ? `AI.REFERENCE_UNAVAILABLE.${reason}` : null;
}

/** i18n key of the toast shown when the user tries to open an unviewable source. */
export function getReferenceUnavailableMessage(
    reference: AISharedReferenceAvailability,
): string | null {
    const reason = getReferenceUnavailableReason(reference);
    return reason ? `AI.REFERENCE_UNAVAILABLE_MESSAGE.${reason}` : null;
}

/**
 * Same reasons as a source, different wording: the toast names the attachment the
 * question was about, not a source the answer drew from.
 */
export function getMessageAttachmentUnavailableMessage(
    attachment: AISharedReferenceAvailability,
): string | null {
    const reason = getReferenceUnavailableReason(attachment);
    return reason ? `AI.ATTACHMENT_UNAVAILABLE_MESSAGE.${reason}` : null;
}

export function isModelMessageError(message: AISharedModelMessage): boolean {
    return aiSharedErrorTypes.has(message.messageType);
}

export function getAIErrorMessage(error: unknown): {
    messageType: AISharedMessageType;
    message: string;
} {
    if (error instanceof HttpErrorResponse) {
        if (error.status === 429) {
            return {
                messageType: AISharedMessageType.TOO_MANY_REQUESTS_ERROR,
                message: 'AI.ERROR_429',
            };
        } else if (error.status === 424) {
            return {
                messageType: AISharedMessageType.GENERIC_ERROR,
                message: 'AI.ERROR_424',
            };
        } else if (error.status === 422) {
            return {
                messageType: AISharedMessageType.CANNOT_ACCESS_INFO_ERROR,
                message: 'AI.ERROR_422',
            };
        } else if (isAttachmentScanPendingError(error)) {
            return {
                messageType: AISharedMessageType.GENERIC_ERROR,
                message: 'AI.ERROR_ATTACHMENT_SCAN_PENDING',
            };
        }
    }
    return {
        messageType: AISharedMessageType.GENERIC_ERROR,
        message: 'AI.ERROR',
    };
}
